<script lang="ts">
  /**
   * The sheet behind the round profile button, composed: who you are, an
   * optional figure of yours, then the game's rows in groups. On the
   * `ProfileSheet` frame (a `Sheet`: Back, Escape and the backdrop close it).
   *
   * - **`who`**: the name and one line under it; a guest gets the generic
   *   person, an account the initial of its name.
   * - **`stat`** (optional): a figure the player owns (coins, a rating),
   *   drawn first in a group of its own: usually one `OptionRow` without
   *   `onclick`, a line of information.
   * - **`groups`**: `ProfileGroup`s of `ProfileRow`s (an icon, a title, a
   *   line of detail, a tone). A row with `onclick` is a button: pressing it
   *   closes the sheet and then calls `onclick` (`keepOpen` skips the
   *   close); without `onclick` it is information.
   * - **`admin`** (optional): one row, drawn last in a group of its own, and
   *   only when the game passes it (the player is on the server's roster).
   *
   * **The guide's rule for the order:** the first group is where the player
   * goes (scores, stats, how to play, settings, install); the account's own
   * actions (change password, sign out) are the **second** group, with
   * `tone: 'danger'` on anything that cannot be taken back; Admin is last.
   * A game whose account actions live elsewhere (in Settings) simply has one
   * group.
   *
   * A row's `icon` is one of the shell's glyphs (an `IconName`) or a snippet
   * of the game's own drawing (a coin), in `currentColor`.
   *
   * Test ids: `testid` on the sheet; `who.testid` on the name; each group's
   * and row's `testid`. No defaults.
   */
  import type { Snippet } from 'svelte';

  import Icon from '../components/Icon.svelte';
  import OptionRow from '../components/OptionRow.svelte';
  import OptionSection from '../components/OptionSection.svelte';
  import ProfileSheet from '../components/ProfileSheet.svelte';
  import type { ProfileGroup, ProfileRow, ProfileWho } from './pageTypes.js';

  interface Props {
    readonly open: boolean;
    /** The sheet's accessible name ("Profile"). */
    readonly label: string;
    readonly onclose: () => void;
    readonly who: ProfileWho;
    /** A figure of the player's, first, in a group of its own. */
    readonly stat?: Snippet | undefined;
    readonly groups: readonly ProfileGroup[];
    /** The Admin row: drawn last, and only when given. */
    readonly admin?: ProfileRow | undefined;
    readonly testid?: string | undefined;
  }
  const { open, label, onclose, who, stat, groups, admin, testid }: Props = $props();

  const initial = $derived.by(() => {
    if (who.state === 'guest') return null;
    const letter = who.initial ?? who.name.trim().charAt(0);
    return letter.length > 0 ? letter.toUpperCase() : null;
  });

  function pressed(row: ProfileRow): (() => void) | undefined {
    const action = row.onclick;
    if (action === undefined) return undefined;
    return () => {
      if (row.keepOpen !== true) onclose();
      action();
    };
  }
</script>

{#snippet rowOf(row: ProfileRow)}
  {#snippet glyph()}
    {#if typeof row.icon === 'string'}
      <Icon name={row.icon} size={20} />
    {:else if row.icon !== undefined}
      {@render row.icon()}
    {/if}
  {/snippet}
  <OptionRow
    title={row.title}
    detail={row.detail}
    icon={row.icon === undefined ? undefined : glyph}
    tone={row.tone ?? 'default'}
    onclick={pressed(row)}
    testid={row.testid}
  />
{/snippet}

<ProfileSheet
  {open}
  {label}
  name={who.name}
  detail={who.detail}
  {initial}
  avatar={who.state === 'guest' ? undefined : who.avatar}
  nameTestid={who.testid}
  {testid}
  {onclose}
>
  {#if stat !== undefined}
    <OptionSection>{@render stat()}</OptionSection>
  {/if}
  {#each groups as group (group.id)}
    <OptionSection label={group.label} testid={group.testid}>
      {#each group.rows as row (row.id)}
        {@render rowOf(row)}
      {/each}
    </OptionSection>
  {/each}
  {#if admin !== undefined}
    <OptionSection>{@render rowOf(admin)}</OptionSection>
  {/if}
</ProfileSheet>
